import getUrlPath from '../utils/getUrlPath';
import { auth } from '../utils/auth';
import { authUrls, url } from './url';
import { closeMessageModal } from '../utils/message';
import { MessageContents } from '../types/message';
import { redirectToCart, redirectToHome } from '../utils/router';

// TODO
// Add internationalization for the messages.
export const messageContents = {
  addressValidationWarning: {
    title: 'Address Validation Warning',
    description:
      'The address you entered could not be validated. Click proceed anyway to continue with what you entered or click close to stay and edit the address.',
    confirmButtonLabel: 'Proceed Anyway',
  },
  missingEquipmentPrice: {
    title: 'Unable to add item to cart: ',
    description:
      'The asset you have selected currently is missing local pricing details. Please contact us for pricing.',
    confirmButtonLabel: '1.888.80.RENTS',
    confirmClickHandler: () => {
      window.location.href = `tel:+18888073687`;
    },
  },
  formSubmissionError: {
    title: 'Form Submission Failed',
    description: 'We encountered an error while submitting your form. Please try again.',
    confirmButtonLabel: 'close',
    confirmClickHandler: closeMessageModal,
  },
  genericErrorMessage: {
    title: 'Something went wrong',
    description: 'Oops, something went wrong. Please try again.',
    confirmButtonLabel: 'close',
    confirmClickHandler: closeMessageModal,
  },
  accountConflictError: {
    title: 'Something went wrong',
    description:
      "We're not able to process your request at this time. Please reach out to support.",
    confirmButtonLabel: 'close',
    confirmClickHandler: closeMessageModal,
  },
  networkError: {
    title: 'Network Error',
    description: 'A network error has occured. Please check your internet connection and retry.',
    confirmButtonLabel: 'close',
    confirmClickHandler: closeMessageModal,
  },
  jobsiteNotExistingError: {
    title: 'Something went wrong',
    description:
      'Oops! Looks like this jobsite was deleted. You can re-enter it or pick a different one.',
    confirmButtonLabel: 'close',
    confirmClickHandler: () => {
      window.location.reload();
    },
  },
  invalidCartItemsError: {
    title: 'Failed to submit order',
    description:
      'Some items in your cart have outdated dates. Please update them to proceed with your order.',
    confirmButtonLabel: 'close',
    confirmClickHandler: redirectToCart,
  },
  notifyLocationUpdateOnJobsiteChange: {
    title: 'Jobsite Address Mismatch',
    description: `
      <div class="description">
        <p class="para">
          The jobsite address you entered doesn't match the location selected for equipment pricing. 
          To ensure accurate fulfillment, we've updated your branch location to match the jobsite address.
        </p>
        <p class="para">
          <strong>Please note: </strong>
          <span>Equipment pricing may be adjusted based on the new branch location.</span>
        </p>
      </div>
    `,
    isDescriptionPrebuiltHtml: true,
    confirmButtonLabel: 'CLOSE & CONTINUE',
  },
  locationNotSelected: {
    title: 'Select location',
    description: 'Please select your location.',
    confirmButtonLabel: 'close',
    confirmClickHandler: closeMessageModal,
  },
  notifyOnNoBranchFound: {
    title: 'Location out of service area',
    description: "Sorry, we're unable to provide service in your location at this time.",
    confirmButtonLabel: 'CLOSE & CONTINUE',
  },
  rentalPdfError: {
    title: 'Error opening order confirmation',
    description: 'Order confirmation could not be opened. Please try again.',
    confirmButtonLabel: 'close',
    confirmClickHandler: closeMessageModal,
  },
  closeOffRentModalMessage: {
    title: 'Are you sure?',
    description:
      'You are about to cancel an existing Pick Up Request. Please select "confirm cancelation" to continue.',
    confirmButtonLabel: 'CONFIRM CANCELLATION',
  },
  rentalModalErrorMessage: {
    title: 'Pickup request failed',
    description: 'Please contact customer service if issue persists.',
    confirmButtonLabel: 'CLOSE',
    confirmClickHandler: closeMessageModal,
  },
  sessionExpiredErrorMessage: {
    title: 'Your session has expired.',
    description:
      'Your login session has expired due to inactivity. Please log in again to continue.',
    confirmButtonLabel: 'CONTINUE TO LOGIN',
    confirmClickHandler: () => {
      auth.loginAfterForceLogout(window.location.href, true);
    },
  },
  invalidCartDateMessage: {
    title: 'Please select future dates',
    description:
      'Your selected rental dates have already passed. Please select future dates to move forward with your reservation.',
    confirmButtonLabel: 'CLOSE',
    confirmClickHandler: closeMessageModal,
  },
  unexpectedErrorMessage: {
    description:
      'An unexpected error occurred. Please try again or contact customer support if the issue persists.',
    confirmButtonLabel: 'close',
    confirmClickHandler: closeMessageModal,
  },
  invalidRequestErrorMessage: {
    description: 'Invalid request. Please verify your details and try again',
    confirmButtonLabel: 'close',
    confirmClickHandler: closeMessageModal,
  },
  firewallBlockErrorMessage: {
    title: 'Blocked request',
    description:
      'Your request was blocked due to security reasons. Please contact customer support if you believe this is an error.',
    confirmButtonLabel: 'close',
    confirmClickHandler: closeMessageModal,
  },
  tokenExpiredErrorMessage: {
    description: 'Your session has expired. Please log in again to continue.',
    confirmButtonLabel: 'close',
    confirmClickHandler: () => {
      const redirectLink = getUrlPath(authUrls.subPath.login);
      auth.loginAfterForceLogout(redirectLink);
    },
    closeClickHandler: closeMessageModal,
  },
  recaptchaValidationErrorMessage: {
    description: 'Unable to verify your request. Please refresh the page and try again.',
    confirmButtonLabel: 'close',
    confirmClickHandler: () => {
      window.location.reload();
    },
    closeClickHandler: closeMessageModal,
  },
  systemFailureErrorMessage: {
    title: 'Something went wrong',
    description: 'System is encountering a problem at this time. Please try again.',
    confirmButtonLabel: 'close',
    confirmClickHandler: closeMessageModal,
  },
  authenticationErrorMessage: {
    description:
      'You’re not authorized to perform this operation. Please reach out to your administrator or customer support.',
    confirmButtonLabel: 'close',
    confirmClickHandler: () => {
      const redirectLink = getUrlPath(authUrls.subPath.login);
      auth.loginAfterForceLogout(redirectLink);
    },
    closeClickHandler: redirectToHome,
  },
  authorizationErrorMessage: {
    description:
      'You’re not authorized to perform this operation. Please reach out to your administrator or customer support.',
    confirmButtonLabel: 'close',
    confirmClickHandler: () => {
      window.location.href = getUrlPath(url.account.id) || '';
    },
    closeClickHandler: () => {
      window.location.href = getUrlPath(url.account.id) || '';
    },
  },
  formAlreadySubmittedErrorMessage: {
    title: 'Credit Application Already Submitted',
    description:
      'Application already submitted for this company. Continue to reapply for the application.',
    confirmButtonLabel: 'CLOSE & CONTINUE',
    confirmClickHandler: () => {
      window.location.href = getUrlPath(url.account.subPath.creditAccount.id) || '';
    },
    closeClickHandler: closeMessageModal,
  },
  formSuccessfullySubmitted: {
    title: 'Thank you for submitting your credit application!',
    description: 'Our team is reviewing your information and will follow up with you soon.',
    confirmButtonLabel: 'CLOSE & CONTINUE',
  },
  creditLimitAlreadySet: {
    title: 'Credit Limit Already Set',
    description: 'Your credit limit has already been set. Redirecting to credit management.',
    confirmButtonLabel: 'CLOSE & CONTINUE',
    confirmClickHandler: () => {
      window.location.href = getUrlPath(url.account.subPath.creditAccount.id) || '';
    },
    closeClickHandler: closeMessageModal,
  },
  creditAuthenticationMessage: {
    title: 'Account required',
    description:
      'An EquipmentShare account is required to submit a commercial credit application. Please continue to sign up or sign in with your account to continue',
    confirmButtonLabel: 'continue to sign up',
  },
  creditApplicationMissingDetails: (props) => {
    return {
      title: 'Submission Incomplete',
      description:
        'We couldn’t process your credit application—some required information is still missing. Please review and complete all necessary fields to continue.',
      confirmButtonLabel: 'Edit Application',
      confirmClickHandler: props.confirmHandler,
    };
  },
  priceMismatchMessage: {
    title: 'checkout.priceMismatchMessage.title',
    description: 'checkout.priceMismatchMessage.description',
    confirmButtonLabel: 'checkout.priceMismatchMessage.confirmButtonLabel',
  },
  setPreferredJobsiteMessage: {
    title: 'Replace Preferred Jobsite',
    description: 'You are about to replace your preferred jobsite. Please confirm or cancel this action.',
    confirmButtonLabel: 'CONFIRM',
    cancelButtonLabel: 'CANCEL',
  },
  documentLoadingFailedMessage: {
    title: 'Something went wrong',
    description:
      'Something went wrong while loading the documents. Please refresh or try again later.',
    confirmButtonLabel: 'close',
    confirmClickHandler: closeMessageModal,
  },
  utilizationLoadingFailedMessage: {
    title: 'Something went wrong',
    description:
      'Something went wrong while loading the data. Please refresh the page or try again later.',
    confirmButtonLabel: 'close',
    confirmClickHandler: closeMessageModal,
  },
} satisfies MessageContents;
